import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import StopIcon from "@material-ui/icons/Stop";
import RefreshIcon from "@material-ui/icons/Refresh";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const PAGE_SIZE = 20;
const STATUS_TABS = ["all", "pending", "transcribed", "noMedia"];

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  statCard: {
    padding: theme.spacing(2),
    height: "100%",
  },
  statValue: {
    fontSize: 26,
    fontWeight: 600,
  },
  jobPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  progress: {
    height: 10,
    borderRadius: 5,
  },
  bodyCell: {
    maxWidth: 420,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  audio: {
    height: 32,
    width: 240,
  },
  errorList: {
    maxHeight: 160,
    overflowY: "auto",
    marginTop: theme.spacing(1),
    fontSize: 12,
    fontFamily: "monospace",
  },
}));

const audioStatus = (m) => {
  if (m.transcrito && m.body && m.body.trim()) return "transcribed";
  if (!m.mediaUrl) return "noMedia";
  return "pending";
};

const STATUS_CHIP_COLOR = {
  transcribed: "primary",
  pending: "default",
  noMedia: "secondary",
};

const t = (key, opts) => i18n.t(`audioTranscriptions.${key}`, opts);

const AudioTranscriptions = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;

  const [stats, setStats] = useState(null);
  const [job, setJob] = useState(null);
  const [config, setConfig] = useState({ concurrency: 1, minConcurrency: 1, maxConcurrency: 10 });
  const [savingConfig, setSavingConfig] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [statusFilter, setStatusFilter] = useState("all");
  const [fromMeFilter, setFromMeFilter] = useState("");
  const [page, setPage] = useState(0);
  const [messages, setMessages] = useState([]);
  const [count, setCount] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [transcribingIds, setTranscribingIds] = useState({});

  const lastJobStatus = useRef(null);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/audio-transcriptions/overview");
      setStats(data.stats);
      setJob(data.job);
      setConfig(data.config);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const loadMessages = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/audio-transcriptions/messages", {
        params: {
          status: statusFilter,
          fromMe: fromMeFilter || undefined,
          pageNumber: page + 1,
        },
      });
      setMessages(data.messages);
      setCount(data.count);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingList(false);
    }
  }, [statusFilter, fromMeFilter, page]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Progresso em tempo real
  useEffect(() => {
    if (!socket || !companyId) return;

    const onEvent = (data) => {
      if (data.action === "progress") setJob(data.job);
    };

    socket.on(`company-${companyId}-audioTranscription`, onEvent);
    return () => {
      socket.off(`company-${companyId}-audioTranscription`, onEvent);
    };
  }, [socket, companyId]);

  // Ao terminar/cancelar, recarrega números e tabela
  useEffect(() => {
    const status = job?.status || null;
    const wasActive = ["running", "paused"].includes(lastJobStatus.current);
    if (wasActive && ["finished", "cancelled"].includes(status)) {
      loadOverview();
      loadMessages();
      if (status === "finished") toast.success(t("toasts.finished"));
    }
    lastJobStatus.current = status;
  }, [job, loadOverview, loadMessages]);

  // Enquanto roda, atualiza os cards periodicamente
  const isActive = job && ["running", "paused"].includes(job.status);
  useEffect(() => {
    if (!isActive) return;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get("/audio-transcriptions/overview");
        setStats(data.stats);
      } catch (err) {
        // silencioso: o progresso continua chegando pelo socket
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [isActive]);

  const jobAction = async (action) => {
    setActionLoading(true);
    try {
      const { data } = await api.post(`/audio-transcriptions/job/${action}`);
      setJob(data);
      if (action === "start" && data.total === 0) {
        toast.info(t("toasts.nothingPending"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleConcurrencyChange = async (value) => {
    setSavingConfig(true);
    try {
      const { data } = await api.put("/audio-transcriptions/config", { concurrency: value });
      setConfig((prev) => ({ ...prev, concurrency: data.concurrency }));
      toast.success(t("toasts.configSaved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTranscribeOne = async (messageId) => {
    setTranscribingIds((prev) => ({ ...prev, [messageId]: true }));
    try {
      const { data } = await api.post(`/audio-transcriptions/messages/${messageId}/transcribe`);
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, ...data } : m)));
      loadOverview();
    } catch (err) {
      toastError(err);
    } finally {
      setTranscribingIds((prev) => {
        const next = { ...prev };
        delete next[messageId];
        return next;
      });
    }
  };

  if (user.profile !== "admin") {
    return (
      <MainContainer>
        <ForbiddenPage />
      </MainContainer>
    );
  }

  const concurrencyOptions = [];
  for (let i = config.minConcurrency; i <= config.maxConcurrency; i++) concurrencyOptions.push(i);

  const percentTranscribed =
    stats && stats.total > 0 ? Math.round((stats.transcribed / stats.total) * 100) : 0;

  const statCards = stats
    ? [
        { label: t("stats.total"), value: stats.total },
        { label: t("stats.transcribed"), value: stats.transcribed, hint: `${percentTranscribed}%` },
        { label: t("stats.pending"), value: stats.pending },
        { label: t("stats.noMedia"), value: stats.noMedia },
        { label: t("stats.received"), value: stats.received },
        { label: t("stats.sent"), value: stats.sent },
      ]
    : [];

  return (
    <MainContainer>
      <MainHeader>
        <Title>{t("title")}</Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title={t("buttons.refresh")}>
            <IconButton
              onClick={() => {
                loadOverview();
                loadMessages();
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          {statCards.map((card) => (
            <Grid item xs={6} sm={4} md={2} key={card.label}>
              <Paper variant="outlined" className={classes.statCard}>
                <Typography variant="body2" color="textSecondary">
                  {card.label}
                </Typography>
                <Typography className={classes.statValue}>
                  {card.value.toLocaleString("pt-BR")}
                </Typography>
                {card.hint && (
                  <Typography variant="caption" color="textSecondary">
                    {card.hint}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper variant="outlined" className={classes.jobPaper}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id="concurrency-label">{t("config.concurrency")}</InputLabel>
                <Select
                  labelId="concurrency-label"
                  label={t("config.concurrency")}
                  value={config.concurrency}
                  disabled={savingConfig}
                  onChange={(e) => handleConcurrencyChange(e.target.value)}
                >
                  {concurrencyOptions.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n === 1 ? t("config.oneRequest") : t("config.manyRequests", { count: n })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={9}>
              <Box display="flex" style={{ gap: 8 }} flexWrap="wrap">
                {!isActive && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrowIcon />}
                    disabled={actionLoading || !stats || stats.pending === 0}
                    onClick={() => jobAction("start")}
                  >
                    {t("buttons.start", { count: stats ? stats.pending : 0 })}
                  </Button>
                )}
                {job?.status === "running" && (
                  <Button
                    variant="outlined"
                    startIcon={<PauseIcon />}
                    disabled={actionLoading}
                    onClick={() => jobAction("pause")}
                  >
                    {t("buttons.pause")}
                  </Button>
                )}
                {job?.status === "paused" && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrowIcon />}
                    disabled={actionLoading}
                    onClick={() => jobAction("resume")}
                  >
                    {t("buttons.resume")}
                  </Button>
                )}
                {isActive && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<StopIcon />}
                    disabled={actionLoading}
                    onClick={() => jobAction("cancel")}
                  >
                    {t("buttons.cancel")}
                  </Button>
                )}
              </Box>
            </Grid>

            {job && (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">
                    <strong>{t(`jobStatus.${job.status}`)}</strong>
                    {" — "}
                    {t("progress.counts", {
                      processed: job.processed,
                      total: job.total,
                      success: job.success,
                      failed: job.failed,
                      skipped: job.skipped,
                    })}
                    {isActive && job.inFlight > 0 && ` — ${t("progress.inFlight", { count: job.inFlight })}`}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{job.percent}%</strong>
                  </Typography>
                </Box>
                <LinearProgress
                  className={classes.progress}
                  variant="determinate"
                  value={job.percent}
                  color={job.status === "paused" ? "secondary" : "primary"}
                />
                {job.errors && job.errors.length > 0 && (
                  <div className={classes.errorList}>
                    {job.errors.map((e) => (
                      <div key={`${e.messageId}-${e.at}`}>
                        #{e.messageId}: {e.error}
                      </div>
                    ))}
                  </div>
                )}
              </Grid>
            )}
          </Grid>
        </Paper>

        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Tabs
            value={statusFilter}
            indicatorColor="primary"
            textColor="primary"
            onChange={(e, value) => {
              setStatusFilter(value);
              setPage(0);
            }}
          >
            {STATUS_TABS.map((s) => (
              <Tab key={s} value={s} label={t(`filters.${s}`)} />
            ))}
          </Tabs>
          <FormControl size="small" variant="outlined" style={{ minWidth: 160 }}>
            <InputLabel id="fromme-label">{t("filters.direction")}</InputLabel>
            <Select
              labelId="fromme-label"
              label={t("filters.direction")}
              value={fromMeFilter}
              onChange={(e) => {
                setFromMeFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">{t("filters.allDirections")}</MenuItem>
              <MenuItem value="false">{t("filters.received")}</MenuItem>
              <MenuItem value="true">{t("filters.sent")}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("table.date")}</TableCell>
              <TableCell>{t("table.contact")}</TableCell>
              <TableCell>{t("table.ticket")}</TableCell>
              <TableCell>{t("table.direction")}</TableCell>
              <TableCell>{t("table.audio")}</TableCell>
              <TableCell>{t("table.status")}</TableCell>
              <TableCell>{t("table.transcription")}</TableCell>
              <TableCell align="center">{t("table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loadingList &&
              messages.map((m) => {
                const status = audioStatus(m);
                return (
                  <TableRow key={m.id}>
                    <TableCell>{format(parseISO(m.createdAt), "dd/MM/yyyy HH:mm")}</TableCell>
                    <TableCell>{m.contact ? m.contact.name || m.contact.number : "-"}</TableCell>
                    <TableCell>{m.ticketId}</TableCell>
                    <TableCell>{m.fromMe ? t("filters.sent") : t("filters.received")}</TableCell>
                    <TableCell>
                      {m.mediaUrl ? (
                        <audio className={classes.audio} controls preload="none" src={m.mediaUrl} />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={STATUS_CHIP_COLOR[status]} label={t(`filters.${status}`)} />
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {status === "transcribed" ? m.body : ""}
                    </TableCell>
                    <TableCell align="center">
                      {status !== "noMedia" && (
                        <Tooltip title={status === "transcribed" ? t("buttons.alreadyTranscribed") : t("buttons.transcribe")}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={status === "transcribed" || !!transcribingIds[m.id]}
                              onClick={() => handleTranscribeOne(m.id)}
                            >
                              {transcribingIds[m.id] ? <CircularProgress size={18} /> : <RecordVoiceOverIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            {loadingList && <TableRowSkeleton columns={8} />}
            {!loadingList && messages.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {t("table.empty")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[]}
          onPageChange={(e, newPage) => setPage(newPage)}
        />
      </Paper>
    </MainContainer>
  );
};

export default AudioTranscriptions;
